import moment from 'moment-timezone'

// Where a chart's time window starts when nothing else sets one.
export const DEFAULT_TIME_RANGE_FROM = 'now-30d'

// "Everything": the open start for datasets whose time filter rides an
// updated_at column, where the 30-day default hides most of the catalog.
export const OPEN_TIME_RANGE_FROM = '2000-01-01'

// Maps the short unit letters used in now-* expressions to moment.js unit strings.
const UNIT_MAP = {
  m: 'minutes',
  h: 'hours',
  d: 'days',
  w: 'weeks',
  M: 'months',
  y: 'years',
}

/**
 * Convert a relative time value (now-30d, now-1h, now) to a fixed local timestamp.
 * Outputs 'YYYY-MM-DD HH:mm:ss' so the backend treats it as local time,
 * avoiding UTC-offset mismatches.
 *
 * If the value is already absolute (no 'now'), it is returned unchanged.
 *
 * @param {string} value - e.g. 'now-30d', 'now-6h', 'now', or '2024-01-01T00:00:00Z'
 * @returns {string}
 */
export function resolveRelativeTime(value) {
  if (!value || !value.includes('now')) return value

  if (/^(now|n)$/i.test(value.trim())) {
    return moment().format('YYYY-MM-DD HH:mm:ss')
  }

  const match = value.match(/^(?:now|n)-(\d+)([mhdwMy])$/)
  if (!match) return value

  const [, amountStr, unitChar] = match
  const unit = UNIT_MAP[unitChar]
  if (!unit) return value

  return moment().subtract(parseInt(amountStr, 10), unit).format('YYYY-MM-DD HH:mm:ss')
}

/**
 * Convert from/to dynamic time values to fixed local timestamps.
 * Used for CSV downloads and PDF reports.
 *
 * @param {string} from - start time (relative or absolute)
 * @param {string} to   - end time (relative or absolute)
 * @returns {{ from: string, to: string }}
 */
export function freezeTimeRange(from, to) {
  return {
    from: resolveRelativeTime(from),
    to: to ? resolveRelativeTime(to) : moment().format('YYYY-MM-DD HH:mm:ss'),
  }
}
